/**
 * Helper to automatically add target="_blank" to external links and document links
 */

// File extensions considered as documents
const documentExtensions = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.zip',
  '.rar',
  '.txt',
  '.csv',
  '.odt',
  '.ods',
  '.odp',
];

/**
 * Determines if a link should open in a new tab
 * @param href - The link URL
 * @returns true if the link is external or points to a document
 */
export function isExternalLinkOrDocument(href: string): boolean {
  // Check if it's a link to a document
  const isDocument = documentExtensions.some((ext) =>
    href.toLowerCase().endsWith(ext),
  );

  if (isDocument) {
    return true;
  }

  // Check if it's an external link
  // Relative links (starting with / or without protocol) are considered internal
  if (href.startsWith('/') || href.startsWith('#') || href.startsWith('?')) {
    return false;
  }

  // Check if the link starts with a protocol
  if (href.startsWith('http://') || href.startsWith('https://')) {
    try {
      const linkUrl = new URL(href);
      const currentUrl = new URL(window.location.href);

      // Compare hostnames
      return linkUrl.hostname !== currentUrl.hostname;
    } catch (e) {
      // In case of parsing error, consider as internal for safety
      return false;
    }
  }

  // By default, consider as internal (relative links without /)
  return false;
}

/**
 * Applies external link behavior to all links within a container
 * @param $container - jQuery element containing links to process
 */
export function applyExternalLinkBehavior($container: JQuery<HTMLElement>): void {
  const $links = $container.find('a');

  $links.each(function () {
    const $link = jQuery(this);
    const href = $link.attr('href');

    if (!href) {
      return;
    }

    // Check if the link already has a target attribute
    if ($link.attr('target')) {
      return;
    }

    const shouldOpenInNewTab = isExternalLinkOrDocument(href);

    if (shouldOpenInNewTab) {
      $link.attr('target', '_blank');
      $link.attr('rel', 'noopener noreferrer');

      // Add visual indication or aria-label for accessibility
      const currentAriaLabel = $link.attr('aria-label');
      if (currentAriaLabel) {
        $link.attr('aria-label', `${currentAriaLabel} (ouvre dans un nouvel onglet)`);
      } else {
        const linkText = $link.text();
        $link.attr('aria-label', `${linkText} (ouvre dans un nouvel onglet)`);
      }
    }
  });
}
